import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { Observable, throwError, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpHelper } from '../helpers/http-helper';
import { IBusinessOperationQualifier, IServerSettings } from '../models/index';
import { ITermByIndustryUsageAndCategory } from '../models/termByIndustryUsageAndCategory.model';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';

@Injectable()
export class RefdataService extends Subject<DataStateChangeEventArgs> {
    private action: string;
    private data: any;
    private settings: IServerSettings;
    private apiUrl: string;
    apiResult: any;
    httpHelper: HttpHelper;
    allQualifiers: IBusinessOperationQualifier[];
    bOList: IBusinessOperationQualifier[];

    constructor(private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService, private storageManagerService: StorageManagerService) {
        super();

        // Define an instance of the http helper class that will be used to create httpHeader for api calls
        this.httpHelper = new HttpHelper(http, authService, storageManagerService);
    }

    // Get a list of Business Operations from RefDataService API
    GetBusinessOperations(): Observable<ITermByIndustryUsageAndCategory[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.CommonApiGatewayUrl;
        this.action = `rds/v1/Terms/industry?industryId=1&termusageId=3&categoryId=41`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
  }

   // Get a list of Active Ingredients types
   getActiveIngredientsType(): Observable<ITermByIndustryUsageAndCategory[]> {
    const headers = this.httpHelper.createHttpHeaders();
    this.settings = this.settingsService.getSettings();
    this.apiUrl = this.settings.CommonApiGatewayUrl;
    this.action = `rds/v1/Terms/industry?industryId=1&termusageId=3&categoryId=61`;

    return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
  }

    // Get a list of Business Operation Qualifiers from RefDataService API
    GetAllBusinessOperationQualifier(): Observable<ITermByIndustryUsageAndCategory[]> {
        const headers = this.httpHelper.createHttpHeaders();
        this.settings = this.settingsService.getSettings();
        this.apiUrl = this.settings.CommonApiGatewayUrl;
        // a placeholder for now, will be changed to get qulifiers
        this.action = `rds/v1/Terms/TermsAssociation?associationTypeCode=1&usageCode=3&parentListName=Business Operation`;

        return this.http.get(`${this.apiUrl}/${this.action}`, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    // Get a list of Business Operations from cache
    GetBusinessOperationsList(): IBusinessOperationQualifier[] {
        this.allQualifiers = this.storageManagerService.read('businessOperationQualifiers');
        this.bOList = this.getDistintList(this.allQualifiers, 'parentTermValueCode');
        return this.bOList;
    }

    // Get a list of Business Operation Qualifier based on business operations from cache
    GetQualifierByBusOper(businessOperationCode: string): IBusinessOperationQualifier[] {
        this.allQualifiers = this.storageManagerService.read('businessOperationQualifiers');
        this.bOList = this.allQualifiers.filter(x => x.parentTermValueCode == businessOperationCode);
        return this.bOList;
    }

    // Get Business Operation description by code
    GetBussinesOperationDescByCode(code: string): string {
        this.allQualifiers = this.storageManagerService.read('businessOperationQualifiers');
        this.bOList = this.allQualifiers.filter(x => x.parentTermValueCode == code);
        return this.bOList[0].parentTermValueName;
    }

    // Get Business Operation Qualifier description by code
    GetBussinesOperationQualifierDescByCode(busOperCode: string, qualCode: string): string {
        this.allQualifiers = this.storageManagerService.read('businessOperationQualifiers');
        this.bOList = this.allQualifiers.filter(x => x.parentTermValueCode === busOperCode && x.childTermValueCode === qualCode);
        return this.bOList.length != 0 ? this.bOList[0].childTermValueName : '';
    }

    // returns a unique list of values based on a certain propery in the array list
    getDistintList(myArr, prop) {
        return myArr.filter((obj, pos, arr) => {
            return arr.map(mapObj =>
                mapObj[prop]).indexOf(obj[prop]) === pos;
        });
    }

    private extractData(response: Response) {
        if (response.status < 200 || response.status >= 300) {
            throw new Error('Bad response status: ' + response.status);
        }

        const body = response;
        return body || {};
    }

    // Error handling
    private handleErrors(error: any): Observable<any> {
      const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
      const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
      error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

      return throwError(error.error);
    }
}
